import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  MulesoftApplicationDTO,
  MulesoftInfrastructureDTO,
  MulesoftKnowledgeDTO,
  MulesoftPlatformDTO,
  MulesoftServiceDTO,
} from '../models/mulesoft';
import {
  MulesoftOffering,
  MulesoftOfferingResponse,
  MulesoftOfferingsResponse,
} from '../models/mulesoft-offering';

export type OfferingVersion = 'draft' | 'published';

@Injectable({
  providedIn: 'root',
})
export class MulesoftService {
  private readonly _http = inject(HttpClient);

  /**
   * Secure proxy endpoint (API Gateway → Lambda → SSM). The Lambda injects
   * `client_id`/`client_secret` + `RCorrelationId` server-side, so the browser
   * never carries the secret.
   */
  private readonly _apiUrl = environment.aws.mulesoftEndpoint;

  /**
   * Headers for every Process-API call: only `Content-Type` — the Lambda adds
   * the secret and a fresh `RCorrelationId` (the browser cannot send
   * `RCorrelationId`: it is not in the API Gateway CORS allow-list).
   */
  private get _headers(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json' });
  }

  /**
   * Load offerings from the Process API.
   *
   * @param version `draft` (all) or `published` (live) content.
   * @param includeKnowledge when true, requests `include=knowledge` so each
   *   offering carries an embedded `knowledgeContent` object.
   */
  getOfferings(
    version: OfferingVersion,
    includeKnowledge = false,
  ): Observable<MulesoftOffering[]> {
    let params = new HttpParams()
      .set('startsWith', 'offerings')
      .set('version', version);
    if (includeKnowledge) {
      params = params.set('include', 'knowledge');
    }

    return this._http
      .get<MulesoftOfferingsResponse>(`${this._apiUrl}/offerings`, {
        headers: this._headers,
        params,
      })
      .pipe(map((response) => response?.offerings ?? []));
  }

  /**
   * Load a single offering directly from the Process API by id, uuid, or slug.
   *
   * The endpoint disambiguates the identifier via query params:
   *  - UUID  → `uuid=true` is mandatory.
   *  - slug  → `folder` (the offering's folder/path) must be provided.
   *  - id    → neither.
   *
   * @param offeringId the id, uuid, or slug of the offering.
   * @param version `draft` (focus edit) or `published` (public detail page).
   * @param options.uuid set when `offeringId` is a uuid.
   * @param options.folder set when `offeringId` is a slug.
   * @param options.includeKnowledge when true (default), requests
   *   `include=knowledge` so the offering carries an embedded `knowledgeContent`.
   */
  getOffering(
    offeringId: string,
    version: OfferingVersion,
    options: { uuid?: boolean; folder?: string; includeKnowledge?: boolean } = {},
  ): Observable<MulesoftOffering | undefined> {
    const { uuid = false, folder, includeKnowledge = true } = options;

    let params = new HttpParams().set('version', version);
    if (uuid) {
      params = params.set('uuid', 'true');
    }
    if (folder) {
      params = params.set('folder', folder);
    }
    if (includeKnowledge) {
      params = params.set('include', 'knowledge');
    }

    return this._http
      .get<MulesoftOfferingResponse>(
        `${this._apiUrl}/offerings/${encodeURIComponent(offeringId)}`,
        { headers: this._headers, params },
      )
      .pipe(map((response) => response?.offering));
  }

  createApplication(application: MulesoftApplicationDTO) {
    return this._http.post<MulesoftApplicationDTO>(
      `${this._apiUrl}/offerings/softwareapplications`,
      application,
      { headers: this._headers },
    );
  }

  editApplication(application: MulesoftApplicationDTO) {
    return this._http.put<MulesoftApplicationDTO>(
      `${this._apiUrl}/offerings/${application.offering.id}/softwareapplications`,
      application,
      { headers: this._headers },
    );
  }

  createInfrastructure(infrastructure: MulesoftInfrastructureDTO) {
    return this._http.post<MulesoftInfrastructureDTO>(
      `${this._apiUrl}/offerings/infrastructures`,
      infrastructure,
      { headers: this._headers },
    );
  }

  editInfrastructure(infrastructure: MulesoftInfrastructureDTO) {
    return this._http.put<MulesoftInfrastructureDTO>(
      `${this._apiUrl}/offerings/${infrastructure.offering.id}/infrastructures`,
      infrastructure,
      { headers: this._headers },
    );
  }

  createPlatform(platform: MulesoftPlatformDTO) {
    return this._http.post<MulesoftPlatformDTO>(
      `${this._apiUrl}/offerings/platforms`,
      platform,
      { headers: this._headers },
    );
  }

  editPlatform(platform: MulesoftPlatformDTO) {
    return this._http.put<MulesoftPlatformDTO>(
      `${this._apiUrl}/offerings/${platform.offering.id}/platforms`,
      platform,
      { headers: this._headers },
    );
  }

  createProfessionalService(professionalService: MulesoftServiceDTO) {
    return this._http.post<MulesoftServiceDTO>(
      `${this._apiUrl}/offerings/professionalservices`,
      professionalService,
      { headers: this._headers },
    );
  }

  editProfessionalService(professionalService: MulesoftServiceDTO) {
    return this._http.put<MulesoftServiceDTO>(
      `${this._apiUrl}/offerings/${professionalService.offering.id}/professionalservices`,
      professionalService,
      { headers: this._headers },
    );
  }
  createKnowledge(knowledge: MulesoftKnowledgeDTO) {
    return this._http.post(`${this._apiUrl}/knowledges`, knowledge, {
      headers: this._headers,
    });
  }

  /**
   * Update knowledge by its numeric id. The Process API update accepts only the
   * numeric knowledge id (not a uuid), so callers must pass `knowledge.id` — the
   * single-offering fetch embeds it (the list embed omits it). No `uuid=true`.
   */
  editKnowledge(knowledge: MulesoftKnowledgeDTO, knowledgeId: string) {
    return this._http.put(
      `${this._apiUrl}/knowledges/${encodeURIComponent(knowledgeId)}`,
      knowledge,
      { headers: this._headers },
    );
  }
}
